import { Routes, Route, Navigate } from "react-router-dom"

// Components
import Layout from "~components/app/Layout"

// Pages
import Dashboard from "~pages/private/dashboard"
import Perks from "~pages/private/perks"
import Perk from "~pages/private/perk"
import Messages from "~pages/private/messages"
import Message from "~pages/private/message"
import Companies from "~pages/private/companies"
import Users from "~pages/private/users"
import User from "~pages/private/user"
import CreatePortfolio from "~pages/private/create-portfolio"
import PooHistory from "~pages/private/poo-history"
import Redemptions from "~pages/private/redemptions"
import UploadFile from "~pages/private/upload-file"
import SupportTickets from "~pages/private/support-tickets"
import SupportTicket from "~pages/private/support-ticket"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Hooks
import usePrivatePath from "~hooks/usePrivatePath"

// Protected route wrapper with RBAC
interface ProtectedRouteProps {
  authorization: string
  element: React.ReactElement
}

function ProtectedRoute({ authorization, element }: ProtectedRouteProps) {
  if (checkAuthorization(authorization)) {
    return element
  }
  return <Navigate to="/app" replace />
}

export default function AppShell() {
  const { isUserLoggedIn, isLoading } = usePrivatePath()

  // Wait for session restore before deciding to redirect
  if (isLoading || !isUserLoggedIn) {
    return null
  }

  // Determine default route based on user permissions
  const getDefaultElement = () => {
    if (checkAuthorization(RBAC_RULES.VISIT_DASHBOARD_PAGE)) {
      return <Dashboard />
    }
    if (checkAuthorization(RBAC_RULES.VISIT_CREATE_PORTFOLIO_POO_PAGE)) {
      return <CreatePortfolio />
    }
    return <Navigate to="/login" replace />
  }

  return (
    <Layout>
      <Routes>
        {/* Default route */}
        <Route index element={getDefaultElement()} />

        {/* Perks */}
        <Route
          path="perks"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_PERKS_PAGE}
              element={<Perks />}
            />
          }
        />
        <Route
          path="perks/:id"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_PERK_DETAIL_PAGE}
              element={<Perk />}
            />
          }
        />

        {/* Messages/Communication */}
        <Route
          path="communication"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_MESSAGES_PAGE}
              element={<Messages />}
            />
          }
        />
        <Route
          path="communication/:id"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_MESSAGE_DETAIL_PAGE}
              element={<Message />}
            />
          }
        />

        {/* Companies */}
        <Route
          path="companies"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_COMPANIES_PAGE}
              element={<Companies />}
            />
          }
        />
        <Route
          path="companies/:id/messages"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.SEE_MESSAGE_COMPANY}
              element={<Messages />}
            />
          }
        />

        {/* Users */}
        <Route
          path="users"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_USERS_PAGE}
              element={<Users />}
            />
          }
        />
        <Route
          path="users/:email"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_USERS_DETAIL_PAGE}
              element={<User />}
            />
          }
        />
        <Route
          path="users/:id/redemptions"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_USER_REDEMPTIONS}
              element={<Redemptions />}
            />
          }
        />

        {/* Portfolio */}
        <Route
          path="create-portfolio"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_CREATE_PORTFOLIO_POO_PAGE}
              element={<CreatePortfolio />}
            />
          }
        />

        {/* Portfolio — validation history (secondary, read-only) */}
        <Route
          path="poo-history"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_POO_HISTORY_PAGE}
              element={<PooHistory />}
            />
          }
        />

        {/* Upload */}
        <Route
          path="upload-file"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_UPLOAD_FILE_PAGE}
              element={<UploadFile />}
            />
          }
        />

        {/* Support Tickets */}
        <Route
          path="support-tickets"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_SUPPORT_TICKETS_PAGE}
              element={<SupportTickets />}
            />
          }
        />
        <Route
          path="support-tickets/:id"
          element={
            <ProtectedRoute
              authorization={RBAC_RULES.VISIT_SUPPORT_TICKET_DETAIL_PAGE}
              element={<SupportTicket />}
            />
          }
        />

        {/* Catch-all within /app */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Layout>
  )
}
